import { Alpine } from '../../core/alpine-core.js'
import { entityDocId, unwrapApiEntity } from '../../core/api-entity.js'
import { mountAdminDatetime } from '../datetime-flatpickr.js';
import { apiFetch } from '../api.js'
import { CURRENT_SEASON } from '../../core/current-season.js';
import { effectiveAdminLeagueId, withLeagueId } from '../league-context.js';

Alpine.data('fixturesEdit', () => ({

  isEditing: false,
  saving: false,
  swapBusy: false,
  leagueName: '',
  placeholderLogo: '/assets/placeholder-player.png',

  typeOptions: ['fixture', 'event', 'break'],
  statusOptions: ['scheduled', 'completed', 'postponed', 'canceled'],

  form: {
    _id: null,
    type: 'fixture',
    startAt: new Date().toISOString(),
    league: 'PDL',
    season: CURRENT_SEASON,
    roundLabel: null,

    homeTeamId: null,
    homeTeamName: null,
    homeTeamLogo: null,

    awayTeamId: null,
    awayTeamName: null,
    awayTeamLogo: null,

    venueId: null,
    venueName: null,
    mapsUrl: null,

    status: 'scheduled',
    matchId: null,
    legsHome: null,
    legsAway: null
  },

  startAtText: '',
  venuePicked: null,
  suggest: { home: [], away: [] },
  search: { home: '', away: '' },
  venueQuery: '', venueSuggest: [],

  _homeTeamObj: null,
  _awayTeamObj: null,

  loading: true,

  deleteOpen: false,
  deleteBusy: false,

  readRouteId() {
    const url = new URL(location.href)
    const path = (url.hash && url.hash.startsWith('#/')) ? url.hash.slice(1) : url.pathname
    const parts = path.split('/').filter(Boolean)
    const idx = parts.indexOf('fixtures')
    if (idx >= 0 && parts[idx + 1] && parts[idx + 2] === 'edit')
      return decodeURIComponent(parts[idx + 1])
    return null
  },

  listHref() {
    return withLeagueId('/admin/fixtures')
  },

  viewUrl() {
    return this.isEditing
      ? withLeagueId(`/admin/fixtures/${encodeURIComponent(this.form._id)}`)
      : this.listHref()
  },

  typeLabel(t) {
    if (t === 'event') return 'Event'
    if (t === 'break') return 'Break week'
    return 'Match'
  },

  typeHint(t) {
    if (t === 'event') return 'League night, AGM, or special occasion.'
    if (t === 'break') return 'No play — blocks the schedule.'
    return 'Head-to-head between two teams.'
  },

  typeIcon(t) {
    if (t === 'event') return 'fa-trophy text-emerald-400'
    if (t === 'break') return 'fa-mug-hot text-amber-400'
    return 'fa-bullseye text-sky-400'
  },

  typeOptionClass(t) {
    if (t === 'event') return 'border-emerald-400/40 bg-emerald-500/15'
    if (t === 'break') return 'border-amber-400/40 bg-amber-500/15'
    return 'border-sky-400/40 bg-sky-500/15'
  },

  async loadLeagueContext() {
    const routeLeague = effectiveAdminLeagueId('PDL')
    if (!this.isEditing) this.form.league = routeLeague
    else if (!this.form.league) this.form.league = routeLeague
    const leagueKey = this.form.league || routeLeague
    try {
      const seasonQs = encodeURIComponent(this.form.season || CURRENT_SEASON)
      const r = await apiFetch(`/leagues/${encodeURIComponent(leagueKey)}?season=${seasonQs}`)
      const d = await r.json().catch(() => ({}))
      if (r.ok && d?.league) {
        this.leagueName = String(d.league.name || leagueKey)
        if (!this.isEditing && d.league.season) this.form.season = String(d.league.season)
      } else {
        this.leagueName = leagueKey
      }
    } catch {
      this.leagueName = leagueKey
    }
  },

  homeTeam() { return this._homeTeamObj },
  awayTeam() { return this._awayTeamObj },
  isSelected(id) { return this.form.homeTeamId === id || this.form.awayTeamId === id },

  // ---- Bangkok timezone helpers ----
  _formatBangkok(isoUtc) {
    try {
      const d = new Date(isoUtc)
      return new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Bangkok',
        year: 'numeric', month: 'short', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false
      }).format(d)
    } catch { return '' }
  },

  _utcIsoToLocalDateAsBangkokWall(isoUtc) {
    const d = new Date(isoUtc)
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Bangkok',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false
    }).formatToParts(d).reduce((acc, p) => (acc[p.type] = p.value, acc), {})
    const y = +parts.year, m = +parts.month, da = +parts.day, h = +parts.hour, mi = +parts.minute
    return new Date(y, m - 1, da, h, mi)
  },

  _localDateAsBangkokWallToUtcIso(localDate) {
    const utcMs = Date.UTC(
      localDate.getFullYear(),
      localDate.getMonth(),
      localDate.getDate(),
      localDate.getHours() - 7,
      localDate.getMinutes()
    )
    return new Date(utcMs).toISOString()
  },

  async init(id = null) {
    const routeId = id ?? this.readRouteId()
    this.isEditing = Boolean(routeId)
    if (this.isEditing) {
      await this.load(routeId)
    } else {
      await this.loadLeagueContext()
    }
    this._applyTeamsFromForm()

    const waitFor = (pred, tries = 40) => new Promise(res => {
      const t = setInterval(() => { if (pred() || --tries <= 0) { clearInterval(t); res() } }, 50)
    })

    await waitFor(() => !!this.$refs.dt)
    const defLocal = this._utcIsoToLocalDateAsBangkokWall(this.form.startAt)

    mountAdminDatetime(this.$refs.dt, {
      defaultDate: defLocal,
      onChange: (selected) => {
        const dt = selected?.[0]
        if (dt) {
          this.form.startAt = this._localDateAsBangkokWallToUtcIso(dt)
          this.startAtText = this._formatBangkok(this.form.startAt)
        }
      },
      onReady: () => { this.startAtText = this._formatBangkok(this.form.startAt) }
    });

    this.loading = false;

    // Force valid state when type changes
    this.$watch('form.type', (t) => {
      // Common resets when leaving a scored fixture
      if (t !== 'fixture') {
        this.form.legsHome = null;
        this.form.legsAway = null;
        this.form.matchId = null;
      }

      if (t === 'break') {
        // Breaks must be scheduled; no teams; no venue
        if (this.form.status !== 'scheduled') this.form.status = 'scheduled';
        this.clearTeam('home');
        this.clearTeam('away');
        this.clearVenue();
      } else if (t === 'event') {
        // Events can have venue (recommended), but have no teams
        this.clearTeam('home');
        this.clearTeam('away');
      } else if (t === 'fixture') {
        // Fixtures: nothing special here; user will pick teams
      }
    });

    // Guard against illegal status switches (e.g. user had a stale value)
    this.$watch('form.status', (s) => {
      if (this.form.type === 'break' && s !== 'scheduled') {
        this.form.status = 'scheduled';
      }
    });

  },

  statusOptionsForType() {
    return this.form.type === 'break'
      ? ['scheduled']
      : this.statusOptions; // ['scheduled','completed','postponed','canceled']
  },

  async load(id) {
    try {
      const r = await apiFetch(`/fixtures/${id}`)
      const d = await r.json()
      if (!r.ok) throw new Error(d.message || 'Failed to load fixture')
      const f = unwrapApiEntity(d, ['fixture', 'item']) || d

      this.form = {
        _id: f._id,
        type: f.type || 'fixture',
        startAt: f.startAt || new Date().toISOString(),
        league: f.league || 'PDL',
        season: f.season || CURRENT_SEASON,
        roundLabel: f.roundLabel || null,
        homeTeamId: f.homeTeamId || null,
        homeTeamName: f.homeTeamName || null,
        homeTeamLogo: f.homeTeamLogo || null,
        awayTeamId: f.awayTeamId || null,
        awayTeamName: f.awayTeamName || null,
        awayTeamLogo: f.awayTeamLogo || null,
        venueId: f.venueId || null,
        venueName: f.venueName || null,
        mapsUrl: f.mapsUrl || null,
        status: f.status || 'scheduled',
        matchId: f.matchId || null,
        legsHome: f.legsHome ?? null,
        legsAway: f.legsAway ?? null
      }

      this._applyTeamsFromForm()
      this.venuePicked = this.form.venueId ? { _id: this.form.venueId, name: this.form.venueName || '—' } : null
      this.startAtText = this._formatBangkok(this.form.startAt)
      if (this._fp) this._fp.setDate(this._utcIsoToLocalDateAsBangkokWall(this.form.startAt), false)
      await this.loadLeagueContext()
    } catch (e) {
      this.$store.toastr.error(e.message || 'Failed to load fixture')
    }
  },

  _applyTeamsFromForm() {
    this._homeTeamObj = (this.form.homeTeamId || this.form.homeTeamName)
      ? { _id: this.form.homeTeamId, name: this.form.homeTeamName, logoUrl: this.form.homeTeamLogo }
      : null
    this._awayTeamObj = (this.form.awayTeamId || this.form.awayTeamName)
      ? { _id: this.form.awayTeamId, name: this.form.awayTeamName, logoUrl: this.form.awayTeamLogo }
      : null
  },

  async searchTeams(side) {
    const q = (side === 'home' ? this.search.home : this.search.away).trim()
    if (!q) { this.suggest[side] = []; return }
    try {
      const league = encodeURIComponent(this.form.league || effectiveAdminLeagueId('PDL'))
      const r = await apiFetch(`/teams?q=${encodeURIComponent(q)}&league=${league}&limit=20&active=true`)
      const d = await r.json()
      if (!r.ok) throw new Error(d.message || 'Team search failed')
      this.suggest[side] = Array.isArray(d.items) ? d.items : d
    } catch (e) {
      console.warn('searchTeams:', e)
      this.$store.toastr.error('Team search failed')
      this.suggest[side] = []
    }
  },

  pickTeam(side, t) {
    if (!t || this.isSelected(t._id)) return
    if (side === 'home') {
      if (t._id === this.form.awayTeamId) return
      this.form.homeTeamId = t._id
      this.form.homeTeamName = t.name
      this.form.homeTeamLogo = t.logoUrl || null
    } else {
      if (t._id === this.form.homeTeamId) return
      this.form.awayTeamId = t._id
      this.form.awayTeamName = t.name
      this.form.awayTeamLogo = t.logoUrl || null
    }
    this._applyTeamsFromForm()
    this.search[side] = ''
    this.suggest[side] = []
  },

  clearTeam(side) {
    if (side === 'home') {
      this.form.homeTeamId = null; this.form.homeTeamName = null; this.form.homeTeamLogo = null
      this._homeTeamObj = null
    } else {
      this.form.awayTeamId = null; this.form.awayTeamName = null; this.form.awayTeamLogo = null
      this._awayTeamObj = null
    }
  },

  async swapTeams() {
    // nothing to do?
    if (!this.form.homeTeamId && !this.form.awayTeamId) return;

    // local (optimistic) swap first
    const f = this.form;
    const before = {
      homeTeamId: f.homeTeamId, homeTeamName: f.homeTeamName, homeTeamLogo: f.homeTeamLogo,
      awayTeamId: f.awayTeamId, awayTeamName: f.awayTeamName, awayTeamLogo: f.awayTeamLogo,
    };

    [f.homeTeamId, f.awayTeamId] = [f.awayTeamId, f.homeTeamId];
    [f.homeTeamName, f.awayTeamName] = [f.awayTeamName, f.homeTeamName];
    [f.homeTeamLogo, f.awayTeamLogo] = [f.awayTeamLogo, f.homeTeamLogo];
    this._applyTeamsFromForm();

    // if not yet created, just toast and stop
    if (!this.isEditing || !this.form._id) {
      this.$store.toastr.info('Teams swapped (not saved yet)');
      return;
    }

    // persist to API
    this.swapBusy = true;
    try {
      const r = await apiFetch(`/fixtures/${encodeURIComponent(this.form._id)}/swap-teams`, {
        method: 'POST'
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.message || 'Swap failed');

      // use server’s response to normalize any denormalized fields
      const fx = unwrapApiEntity(d, ['fixture', 'item']) || d;
      this.form.homeTeamId = fx.homeTeamId ?? null;
      this.form.homeTeamName = fx.homeTeamName ?? null;
      this.form.homeTeamLogo = fx.homeTeamLogo ?? null;
      this.form.awayTeamId = fx.awayTeamId ?? null;
      this.form.awayTeamName = fx.awayTeamName ?? null;
      this.form.awayTeamLogo = fx.awayTeamLogo ?? null;
      this._applyTeamsFromForm();

      this.$store.toastr.success('Teams swapped & saved');
    } catch (e) {
      // rollback if server failed
      this.form.homeTeamId = before.homeTeamId;
      this.form.homeTeamName = before.homeTeamName;
      this.form.homeTeamLogo = before.homeTeamLogo;
      this.form.awayTeamId = before.awayTeamId;
      this.form.awayTeamName = before.awayTeamName;
      this.form.awayTeamLogo = before.awayTeamLogo;
      this._applyTeamsFromForm();

      this.$store.toastr.error(e.message || 'Swap failed');
    } finally {
      this.swapBusy = false;
    }
  },

  async searchVenues() {
    const q = this.venueQuery.trim()
    if (!q) { this.venueSuggest = []; return }
    try {
      const r = await apiFetch(`/venues?q=${encodeURIComponent(q)}&limit=20`)
      const d = await r.json()
      if (!r.ok) throw new Error(d.message || 'Venue search failed')
      this.venueSuggest = Array.isArray(d.items) ? d.items : d
    } catch (e) {
      console.warn('searchVenues:', e)
      this.$store.toastr.error('Venue search failed')
      this.venueSuggest = []
    }
  },

  pickVenue(v) {
    this.form.venueId = v._id
    this.form.venueName = v.name || null
    this.venuePicked = v
    this.venueQuery = ''
    this.venueSuggest = []
  },

  clearVenue() {
    this.form.venueId = null
    this.form.venueName = null
    this.venuePicked = null
  },

  validate() {
    if (!this.form.startAt) throw new Error('Start date/time is required.');

    if (this.form.type === 'break') {
      if (this.form.status !== 'scheduled') {
        throw new Error('Breaks can only be “scheduled”.');
      }
      // Ensure no teams/venue on break
      this.form.homeTeamId = this.form.awayTeamId = null;
      this.form.venueId = null;
    }

    if (this.form.type === 'event') {
      // If you want to require a venue for events, enforce here:
      // if (!this.form.venueId) throw new Error('Venue is required for events.');
    }

    if (this.form.type === 'fixture') {
      if (!this.form.homeTeamId || !this.form.awayTeamId)
        throw new Error('Select both teams.');
      if (this.form.homeTeamId === this.form.awayTeamId)
        throw new Error('Home and Away cannot be the same team.');
      if (this.form.status === 'completed') {
        if (this.form.legsHome == null || this.form.legsAway == null)
          throw new Error('Enter legs for both teams when completed.');
      }
    }
  },

  async save() {
    this.saving = true
    try {
      this.validate()

      const isBreak = this.form.type === 'break';
      const isFixture = this.form.type === 'fixture';

      const payload = {
        type: this.form.type,
        startAt: this.form.startAt,
        league: this.form.league,
        season: this.form.season,
        roundLabel: this.form.roundLabel,
        homeTeamId: isFixture ? this.form.homeTeamId : null,
        awayTeamId: isFixture ? this.form.awayTeamId : null,
        homeTeamName: this.form.homeTeamName,
        awayTeamName: this.form.awayTeamName,
        venueId: isBreak ? null : this.form.venueId,
        mapsUrl: isBreak ? null : this.form.mapsUrl,
        status: isBreak ? 'scheduled' : this.form.status,
        matchId: isFixture ? this.form.matchId : null,
        legsHome: (isFixture && this.form.status === 'completed') ? (this.form.legsHome ?? 0) : null,
        legsAway: (isFixture && this.form.status === 'completed') ? (this.form.legsAway ?? 0) : null
      };

      let r, d
      if (!this.isEditing) {
        r = await apiFetch(`/fixtures`, { method: 'POST', body: payload })
      } else {
        r = await apiFetch(`/fixtures/${this.form._id}`, { method: 'PATCH', body: payload })
      }

      d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.message || 'Save failed')

      const f = unwrapApiEntity(d, ['fixture', 'item']) || d
      const fid = entityDocId(f)
      if (!this.isEditing) {
        if (!fid) throw new Error('Save failed')
        this.form._id = fid
        this.isEditing = true
        history.replaceState({}, '', withLeagueId(`/admin/fixtures/${fid}/edit`))
        this.$store.toastr.success('Fixture created')
      } else {
        this.$store.toastr.success('Fixture updated')
      }
    } catch (e) {
      this.$store.toastr.error(e.message || 'Save failed')
    } finally {
      this.saving = false
    }
  },

  confirmDelete() { this.deleteOpen = true; },
  async doDelete() {
    if (!this.form._id || this.deleteBusy) return;
    this.deleteBusy = true;
    try {
      const res = await apiFetch(`/fixtures/${this.form._id}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Delete failed');

      this.$store.toastr.success(`Fixture deleted`);
      this.deleteOpen = false;
      this.deleteBusy = false;
      location.href = this.listHref();

    } catch (e) {
      this.$store.toastr.error(e.message || 'Delete failed');
      this.deleteOpen = false;
    } finally {
      this.deleteBusy = false;
    }
  },

}));
